<template>
  <header
    class="navbar"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    :style="{
      opacity: backgroundOpacity,
      backdropFilter: 'blur(6px)',
      boxShadow: scrollY > 100 ? '0 4px 12px rgba(0,0,0,0.25)' : 'none',
      borderBottom: scrollY > 100 ? '1px solid rgba(255,255,255,0.1)' : '1px solid transparent'
    }"
  >
    <!-- Logo -->
    <router-link to="/" class="logo">
      <img src="@/assets/images/osmanlicalogo3.png" alt="Logo" class="logo-image" />
    </router-link>

    <!-- Hamburger Menü (sol altta) -->
    <button class="hamburger fixed-hamburger" @click.stop="toggleMobileMenu">☰</button>

    <!-- Menü -->
    <ul class="main-menu" :class="{ show: mobileMenuOpen }">
      <li><router-link to="/">{{ $t('nav.home') }}</router-link></li>

      <li class="dropdown-wrapper" @mouseenter="hoveredMenu = 'services'" @mouseleave="hoveredMenu = null">
        <span class="dropdown-toggle">{{ $t('nav.services') }}</span>
          <ul v-show="hoveredMenu === 'services'" class="dropdown-menu submenu">
            <li><router-link to="/services/resmi-belge-ceviri">{{ $t('nav.official') }}</router-link></li>
            <li><router-link to="/services/yeminli-ceviri">{{ $t('nav.sworn') }}</router-link></li>
            <li><router-link to="/services/noter-tasdikli-ceviri">{{ $t('nav.notary') }}</router-link></li>
            <li><router-link to="/services/apostil-tasdikli-ceviri">{{ $t('nav.apostille') }}</router-link></li>
          </ul>
      </li>

      <li><router-link to="/quote">{{ $t('nav.quote') }}</router-link></li>
      <li><router-link to="/apply">{{ $t('nav.apply') }}</router-link></li>
      <li><router-link to="/translators">{{ $t('nav.translators') }}</router-link></li>
      <li><router-link to="/about">{{ $t('nav.about') }}</router-link></li>
      <li><router-link to="/contact">{{ $t('nav.contact') }}</router-link></li>
    </ul>

    <!-- Sağ Menü -->
    <div class="right-menu">
      <div class="dropdown-wrapper" @mouseenter="cancelHideLogin" @mouseleave="delayedHideLogin">
        <span class="dropdown-toggle">
          <template v-if="user">👤 {{ user.name }}</template>
          <template v-else>{{ $t('login.title') }}</template>
        </span>
        <transition name="fade-slide">
          <ul v-show="showLoginMenu" class="dropdown-menu">
            <template v-if="user">
              <li><router-link :to="panelRoute" class="dropdown-item">{{ $t('nav.panel') }}</router-link></li>
              <li><router-link to="/settings" class="dropdown-item">{{ $t('nav.settings') }}</router-link></li>
              <li><a href="#" class="dropdown-item" @click.prevent="handleLogout">{{ $t('nav.logout') }}</a></li>
            </template>
            <template v-else>
              <li><router-link to="/login" class="dropdown-item">{{ $t('login.title') }}</router-link></li>
              <li><router-link to="/register" class="dropdown-item">{{ $t('login.register') }}</router-link></li>
            </template>
          </ul>
        </transition>
      </div>

      <!-- Dil Bayrakları -->
      <div class="lang-flags">
        <img src="/icons/tr.png" alt="TR" class="lang-flag" @click="setLanguage('tr')" />
        <img src="/icons/eng.png" alt="EN" class="lang-flag" @click="setLanguage('en')" />
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/user'
import { storeToRefs } from 'pinia'

const router = useRouter()
const route = useRoute()
const { locale } = useI18n()

const scrollY = ref(0)
const hoveredMenu = ref(null)
const isHovered = ref(false)
const mobileMenuOpen = ref(false)
const showLoginMenu = ref(false)

const userStore = useUserStore()
const { user } = storeToRefs(userStore)

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

function handleClickOutside(event) {
  const menu = document.querySelector('.main-menu')
  const button = document.querySelector('.fixed-hamburger')
  if (
    mobileMenuOpen.value &&
    menu &&
    !menu.contains(event.target) &&
    !button.contains(event.target)
  ) {
    mobileMenuOpen.value = false
  }
}

let loginHideTimeout = null
const loginMenuHideDelay = 1000

function delayedHideLogin() {
  loginHideTimeout = setTimeout(() => {
    showLoginMenu.value = false
  }, loginMenuHideDelay)
}

function cancelHideLogin() {
  clearTimeout(loginHideTimeout)
  showLoginMenu.value = true
}

function handleLogout() {
  userStore.logout()
  router.push('/')
  window.location.reload()
}

function setLanguage(lang) {
  locale.value = lang
  localStorage.setItem('lang', lang)
}

const panelRoute = computed(() => {
  if (!user.value) return '/'
  if (user.value.role === 'admin') return '/panel/admin'
  if (user.value.role === 'translator') return '/panel/translator'
  return '/panel/user'
})

function handleScroll() {
  scrollY.value = window.scrollY
}

const backgroundOpacity = computed(() => {
  if (isHovered.value) return 1
  if (route.path !== '/') return '0.96'
  const raw = scrollY.value / 1000
  return Math.min(0.1 + raw, 1).toFixed(2)
})

onMounted(() => {
  userStore.loadUser()
  window.addEventListener('login-success', userStore.loadUser)
  window.addEventListener('scroll', handleScroll)
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('login-success', userStore.loadUser)
  document.removeEventListener('click', handleClickOutside)
})

watch(() => route.path, () => {
  mobileMenuOpen.value = false
})
</script>

<style scoped>
.navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  border-bottom: 1px solid transparent;
  background-image: url('@/assets/images/backgroundos.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: all 0.6s ease;
}

.logo {
  display: flex;
  align-items: center;
  height: 48px;
}

.logo img.logo-image {
  height: 100%;
  max-height: 48px;
  width: auto;
  transform: scale(3.5);
  transform-origin: left center;
  object-fit: contain;
}

.fixed-hamburger {
  position: fixed;
  bottom: 12px;
  left: 12px;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 26px;
  color: white;
  display: none;
}

.main-menu {
  display: flex;
  align-items: center;
  gap: 16px;
  list-style: none;
  margin: 0 auto;
  padding: 0;
}

.main-menu li {
  position: relative;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 14px;
}

.main-menu li:not(:last-child)::after {
  content: "|";
  color: #f04d34;
  margin-left: 12px;
  margin-right: 12px;
  font-weight: normal;
}

.main-menu a {
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
}

.main-menu a:hover {
  color: #f04d34;
}

.right-menu {
  display: flex;
  align-items: center;
  gap: 15px;
}

.dropdown-wrapper {
  position: relative;
}

.dropdown-toggle {
  cursor: pointer;
  color: white;
  font-weight: 600;
  text-transform: uppercase;
}

.dropdown-menu {
  position: absolute;
  top: 110%;
  right: 0;
  background-color: rgba(127, 117, 86, 0.95);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  padding: 6px 0;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  z-index: 999;
}

.dropdown-item {
  padding: 10px 16px;
  color: white;
  text-decoration: none;
  font-size: 14px;
}

.dropdown-item:hover {
  background-color: #a0915c;
}

.lang-flags {
  display: flex;
  gap: 8px;
}

.lang-flag {
  width: 20px;
  height: 14px;
  cursor: pointer;
  border-radius: 2px;
  border: 1px solid #ccc;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.submenu {
  top: 100%;
  left: 0;
  min-width: 220px;
  position: absolute;
  background-color: rgba(127, 117, 86, 0.95);
  backdrop-filter: blur(4px);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 999;
  padding: 8px 0;
}

.submenu li a {
  display: block;
  padding: 10px 16px;
  color: white;
  text-decoration: none;
  font-size: 14px;
}

.submenu li a:hover {
  background-color: #a0915c;
}

/* Mobil görünüm */
@media (max-width: 768px) {
  .fixed-hamburger {
    display: block;
  }

  .main-menu {
    display: none;
    flex-direction: column;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    background-color: rgba(25, 25, 25, 0.97);
    backdrop-filter: blur(6px);
    z-index: 9998;
    height: 100vh;
    padding-top: 100px;
    font-family: 'Segoe UI', sans-serif;
    transition: transform 0.3s ease;
  }

  .main-menu.show {
    display: flex;
  }

  .main-menu li {
    width: 100%;
    padding: 18px 24px;
    font-size: 16px;
    font-weight: 500;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .main-menu li:not(:last-child)::after {
    content: none;
  }

  .right-menu {
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    width: 100%;
    margin-top: 10px;
  }

  .dropdown-menu.submenu {
    position: static;
    background-color: rgba(255, 255, 255, 0.05);
  }

  .dropdown-item,
  .submenu li a {
    padding: 12px 20px;
    width: 100%;
  }

  .lang-flags {
    justify-content: flex-end;
    padding-right: 10px;
  }
}
</style>
